import { motion, AnimatePresence } from "framer-motion";
import { useLocation, Link } from "react-router-dom";
import HeadlessTippy from "@tippyjs/react/headless";
import {
  LayoutDashboard,
  ChartLine,
  ContactRound,
  Briefcase,
  Globe,
  Users,
  FolderKanban,
  Database,
  FileUp,
  RefreshCw,
  UserCog,
  FolderLock,
  Inbox,
  Mail,
  IdCard,
  MapPin,
  Monitor,
  Banknote,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  MessagesSquare,
  Megaphone,
} from "lucide-react";

import { useEffect, useMemo, useRef, useState } from "react";
import { ROLES } from "../../constants";
import { getUserProfile } from "../../controllers/utilityController";
import { useNotices } from "../notices/NoticeProvider";

// Menu entries are one of:
//   { label, icon, href, badge?, badgeKey? }  top-level link (badge renders a green count pill;
//                                  badgeKey "notices" shows the unread notice count)
//   { divider: true }              horizontal separator
//   { label, icon, items, defaultOpen? }  collapsible section; items are { label, icon, href }
const ADMIN_MENU = [
  { label: "Overview", icon: LayoutDashboard, href: "/admin/dashboard" },
  { label: "Performance Dashboard", icon: ChartLine, href: "/admin/performance-dashboard" },
  { label: "HR Dashboard", icon: ContactRound, href: "/hr/dashboard" },
  { label: "Chat", icon: MessagesSquare, href: "/admin/chat" },
  { label: "Notice Board", icon: Megaphone, href: "/notices", badgeKey: "notices" },
  { divider: true },
  {
    label: "Operations",
    icon: Briefcase,
    defaultOpen: true,
    items: [
      { label: "Records", icon: Database, href: "/admin/database-mangement", collapseOnNavigate: true },
      { label: "Import Data", icon: FileUp, href: "/admin/import-data" },
      { label: "SalesQL Sync", icon: RefreshCw, href: "/admin/salesql-sync" },
      { label: "User Management", icon: UserCog, href: "/admin/user-management" },
      { label: "Project Management", icon: FolderKanban, href: "/admin/project-management" },
      { label: "Closed Project", icon: FolderLock, href: "/admin/closed-projects" },
    ],
  },
  {
    label: "Website Section",
    icon: Globe,
    items: [
      { label: "Expert Requests", icon: Inbox, href: "/admin/expert-requests" },
      { label: "Contact Form Queries", icon: Mail, href: "/admin/contact-queries" },
    ],
  },
  {
    label: "HRM",
    icon: Users,
    items: [
      { label: "Employees", icon: IdCard, href: "/hr/employees" },
      { label: "Locations", icon: MapPin, href: "/hr/locations" },
      { label: "Assets", icon: Monitor, href: "/hr/assets" },
      { label: "Payroll", icon: Banknote, href: "/hr/payroll" },
    ],
  },
];

const EMPLOYEE_MENU = [
  { label: "Dashboard", icon: LayoutDashboard, href: "/employee/dashboard" },
  { label: "Performance Dashboard", icon: ChartLine, href: "/admin/performance-dashboard" },
  { label: "Chat", icon: MessagesSquare, href: "/admin/chat" },
  { label: "Notice Board", icon: Megaphone, href: "/notices", badgeKey: "notices" },
  { divider: true },
  {
    label: "Management",
    icon: Briefcase,
    defaultOpen: true,
    items: [
      { label: "Database Management", icon: Database, href: "/admin/database-mangement" },
      { label: "Project Management", icon: FolderKanban, href: "/admin/project-management" },
      { label: "Closed Project", icon: FolderLock, href: "/admin/closed-projects" },
    ],
  },
];

const HR_MENU = [
  { label: "Overview", icon: LayoutDashboard, href: "/hr/dashboard" },
  { label: "Chat", icon: MessagesSquare, href: "/admin/chat" },
  { label: "Notice Board", icon: Megaphone, href: "/notices", badgeKey: "notices" },
  { divider: true },
  {
    label: "HR Management",
    icon: Users,
    defaultOpen: true,
    items: [
      { label: "Employees", icon: IdCard, href: "/hr/employees" },
      { label: "Locations", icon: MapPin, href: "/hr/locations" },
      { label: "Assets", icon: Monitor, href: "/hr/assets" },
      { label: "Payroll", icon: Banknote, href: "/hr/payroll" },
    ],
  },
];

// Shared with the phone menu (MobileNav.jsx), which shows the same entries as a top-level menu.
export const MENU_BY_ROLE = {
  [ROLES.ADMIN]: ADMIN_MENU,
  [ROLES.HR]: HR_MENU,
  [ROLES.EMPLOYEE]: EMPLOYEE_MENU,
  [ROLES.MANAGER]: EMPLOYEE_MENU,
};

const OPEN_SECTIONS_KEY = "sidebarOpenSections";
export const NARROW_QUERY = "(max-width: 1023px)";

// Ctrl/Cmd/Shift/middle clicks open a new tab or window, so the sidebar here shouldn't react.
const isPlainLeftClick = (e) =>
  e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;

const focusRing =
  "outline-none focus-visible:ring-2 focus-visible:ring-app-primary focus-visible:ring-offset-1";
const itemBase = `group relative flex w-full items-center gap-3 h-12 rounded-xl text-base font-medium transition-colors duration-150 ${focusRing}`;
const itemIdle =
  "text-app-secondary hover:bg-blue-50 hover:text-app-primary dark:hover:bg-blue-500/10";
const itemActive = "bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400";
const iconIdle = "text-app-muted group-hover:text-app-primary";
// Submenu rows: same outline icon set/stroke as the main items, one step smaller.
const SUB_ICON_SIZE = 18;
const subItemBase = `group flex items-center gap-2.5 rounded-lg px-3 text-[15px] transition-colors duration-150 ${focusRing}`;
const subItemIdle = "text-app-secondary hover:bg-blue-50 hover:text-app-primary dark:hover:bg-blue-500/10";
const subItemActive = "bg-blue-50 font-medium text-blue-700 dark:bg-blue-500/10 dark:text-blue-400";

function SubItemContent({ item, active }) {
  const Icon = item.icon;
  return (
    <>
      <Icon
        size={SUB_ICON_SIZE}
        strokeWidth={1.75}
        aria-hidden="true"
        className={`shrink-0 transition-colors duration-150 ${active ? iconActive : iconIdle}`}
      />
      <span className="truncate">{item.label}</span>
    </>
  );
}
const iconActive = "text-blue-600 dark:text-blue-400";

const popIn = {
  initial: { opacity: 0, x: -6 },
  animate: { opacity: 1, x: 0 },
  transition: { duration: 0.15, ease: "easeOut" },
};

// Brand-blue label tooltip for the collapsed icon rail.
function RailTooltip({ label, disabled, children }) {
  return (
    <HeadlessTippy
      placement="right"
      offset={[0, 14]}
      delay={[120, 0]}
      disabled={disabled}
      appendTo={() => document.body}
      render={(attrs) => (
        <motion.div
          {...attrs}
          {...popIn}
          role="tooltip"
          className="relative rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white shadow-md shadow-blue-600/20 dark:bg-blue-500"
        >
          {label}
          <span
            data-popper-arrow=""
            className="absolute -left-1 h-2 w-2 before:absolute before:inset-0 before:rotate-45 before:rounded-[1px] before:bg-blue-600 dark:before:bg-blue-500"
          />
        </motion.div>
      )}
    >
      {children}
    </HeadlessTippy>
  );
}

// Longest matching href wins, so nested routes (e.g. /hr/employees/12/edit)
// highlight their parent entry without also lighting up shorter prefixes.
export const findActiveHref = (menu, pathname) => {
  const hrefs = menu.flatMap((entry) =>
    entry.items ? entry.items.map((i) => i.href) : entry.href ? [entry.href] : [],
  );
  return hrefs
    .filter((href) => pathname === href || pathname.startsWith(`${href}/`))
    .sort((a, b) => b.length - a.length)[0];
};

function SidebarComponent({ collapsed, setCollapsed }) {
  const location = useLocation();
  const unreadNotices = useNotices()?.unreadCount || 0;
  const scrollRef = useRef(null);
  const [role, setRole] = useState(null);

  const menu = useMemo(() => MENU_BY_ROLE[role] || [], [role]);
  const activeHref = findActiveHref(menu, location.pathname);
  const homeHref = menu.find((entry) => entry.href)?.href || "/";

  const [openSections, setOpenSections] = useState(() => {
    try {
      const raw = localStorage.getItem(OPEN_SECTIONS_KEY);
      return raw ? new Set(JSON.parse(raw)) : null;
    } catch {
      return null;
    }
  });

  // No saved preference yet: start from each section's defaultOpen.
  useEffect(() => {
    if (openSections === null && menu.length > 0) {
      setOpenSections(new Set(menu.filter((e) => e.defaultOpen).map((e) => e.label)));
    }
  }, [menu, openSections]);

  // Make sure the section holding the current page is expanded.
  useEffect(() => {
    const section = menu.find((e) => e.items?.some((i) => i.href === activeHref));
    if (section) {
      setOpenSections((prev) =>
        prev?.has(section.label) ? prev : new Set([...(prev || []), section.label]),
      );
    }
  }, [menu, activeHref]);

  useEffect(() => {
    if (openSections === null) return;
    try {
      localStorage.setItem(OPEN_SECTIONS_KEY, JSON.stringify([...openSections]));
    } catch {
      // storage unavailable; open state just won't persist
    }
  }, [openSections]);

  const isOpen = (label) => Boolean(openSections?.has(label));

  const toggleSection = (label) => {
    setOpenSections((prev) => {
      const next = new Set(prev || []);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const openSectionFromRail = (label) => {
    setCollapsed(false);
    setOpenSections((prev) => new Set([...(prev || []), label]));
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await getUserProfile();
        setRole(res.role);
      } catch (err) {
        console.error("Failed to fetch role:", err);
      }
    };
    fetchProfile();
  }, []);

  // Collapse to the icon rail on tablet/mobile widths.
  useEffect(() => {
    const mq = window.matchMedia(NARROW_QUERY);
    if (mq.matches) setCollapsed(true);
    const onChange = (e) => setCollapsed(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [setCollapsed]);

  // --- Restore scroll position
  useEffect(() => {
    const saved = sessionStorage.getItem("sidebarScroll");
    if (scrollRef.current && saved)
      scrollRef.current.scrollTop = parseInt(saved, 10);
  }, []);
  const handleScroll = () => {
    if (scrollRef.current)
      sessionStorage.setItem("sidebarScroll", scrollRef.current.scrollTop);
  };

  const renderLink = (entry) => {
    const active = entry.href === activeHref;
    const Icon = entry.icon;
    const badge = entry.badgeKey === "notices" ? unreadNotices || null : entry.badge;
    return (
      <li key={entry.href}>
        <RailTooltip label={entry.label} disabled={!collapsed}>
          <Link
            to={entry.href}
            aria-current={active ? "page" : undefined}
            aria-label={collapsed ? entry.label : undefined}
            className={`${itemBase} px-[18px] ${
              active ? itemActive : itemIdle
            }`}
          >
            {active && (
              <motion.span
                layoutId="sidebarActiveIndicator"
                className="absolute left-0 top-3 bottom-3 w-[3px] rounded-full bg-blue-600 dark:bg-blue-400"
                transition={{ type: "spring", stiffness: 380, damping: 30 }}
              />
            )}
            <span className="relative inline-flex shrink-0">
              <Icon
                size={20}
                strokeWidth={1.75}
                className={`shrink-0 ${active ? iconActive : iconIdle}`}
              />
              {collapsed && badge != null && (
                <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-blue-600 ring-2 ring-white dark:bg-sky-400 dark:ring-gray-900" aria-hidden="true" />
              )}
            </span>
            {!collapsed && <span className="truncate">{entry.label}</span>}
            {!collapsed && badge != null && (
              <span className="ml-auto inline-flex h-7 min-w-[32px] items-center justify-center rounded-full bg-green-50 px-2 text-sm font-medium text-green-700 dark:bg-green-500/10 dark:text-green-400">
                {badge}
              </span>
            )}
          </Link>
        </RailTooltip>
      </li>
    );
  };

  const renderChildLink = (item, isLast) => {
    const active = item.href === activeHref;
    return (
      <li
        key={item.href}
        className={`relative pl-5 py-0.5 before:absolute before:left-0 before:top-0 before:h-1/2 before:w-3 before:rounded-bl-lg before:border-b before:border-l before:border-app-primary ${
          isLast
            ? ""
            : "after:absolute after:left-0 after:top-1/2 after:bottom-0 after:border-l after:border-app-primary"
        }`}
      >
        <Link
          to={item.href}
          onClick={(e) => {
            if (item.collapseOnNavigate && isPlainLeftClick(e)) setCollapsed(true);
          }}
          aria-current={active ? "page" : undefined}
          className={`${subItemBase} h-10 ${active ? subItemActive : subItemIdle}`}
        >
          <SubItemContent item={item} active={active} />
        </Link>
      </li>
    );
  };

  const renderSection = (entry) => {
    const Icon = entry.icon;
    const open = isOpen(entry.label);
    const hasActiveChild = entry.items.some((i) => i.href === activeHref);
    const listId = `sidebar-section-${entry.label.replace(/\s+/g, "-").toLowerCase()}`;

    if (collapsed) {
      return (
        <li key={entry.label}>
          <HeadlessTippy
            interactive
            interactiveBorder={16}
            placement="right-start"
            offset={[-6, 14]}
            delay={[80, 60]}
            appendTo={() => document.body}
            render={(attrs, _content, instance) => (
              <motion.div
                {...attrs}
                {...popIn}
                className="w-60 rounded-xl border border-app-primary bg-app-sidebar p-1.5 shadow-lg shadow-slate-900/[0.06] dark:shadow-black/30"
              >
                <div className="mb-1 flex items-center gap-2 border-b border-app-primary px-3 pb-2.5 pt-2">
                  <Icon size={16} strokeWidth={1.75} className="text-blue-600 dark:text-blue-400" />
                  <span className="text-sm font-semibold text-app-heading">{entry.label}</span>
                </div>
                <ul className="flex flex-col gap-0.5">
                  {entry.items.map((item) => {
                    const active = item.href === activeHref;
                    return (
                      <li key={item.href}>
                        <Link
                          to={item.href}
                          onClick={() => instance?.hide()}
                          aria-current={active ? "page" : undefined}
                          className={`${subItemBase} h-10 ${active ? subItemActive : subItemIdle}`}
                        >
                          <SubItemContent item={item} active={active} />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </motion.div>
            )}
          >
            <button
              type="button"
              onClick={() => openSectionFromRail(entry.label)}
              aria-label={`${entry.label} (expand sidebar)`}
              className={`${itemBase} px-[18px] ${hasActiveChild ? itemActive : itemIdle}`}
            >
              <Icon
                size={20}
                strokeWidth={1.75}
                className={`shrink-0 ${hasActiveChild ? iconActive : iconIdle}`}
              />
            </button>
          </HeadlessTippy>
        </li>
      );
    }

    return (
      <li key={entry.label}>
        <button
          type="button"
          onClick={() => toggleSection(entry.label)}
          aria-expanded={open}
          aria-controls={listId}
          className={`${itemBase} px-[18px] ${itemIdle}`}
        >
          <Icon
            size={20}
            strokeWidth={1.75}
            className={`shrink-0 ${hasActiveChild ? iconActive : iconIdle}`}
          />
          <span className="truncate">{entry.label}</span>
          <motion.span
            className="ml-auto text-app-muted"
            animate={{ rotate: open ? 180 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronDown size={18} strokeWidth={1.75} />
          </motion.span>
        </button>

        <AnimatePresence initial={false}>
          {open && (
            <motion.ul
              id={listId}
              key="items"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="ml-[26px] overflow-hidden pr-1"
            >
              {entry.items.map((item, i) =>
                renderChildLink(item, i === entry.items.length - 1),
              )}
            </motion.ul>
          )}
        </AnimatePresence>
      </li>
    );
  };

  return (
    // Width is a CSS transition on a fixed element: it never reflows the page and
    // doesn't depend on JS frames, so heavy route renders can't stall it.
    <aside
      style={{ width: collapsed ? 80 : 300 }}
      className="h-screen flex flex-col bg-app-sidebar border-r border-app-primary fixed top-0 left-0 z-50 transition-[width] duration-300 ease-in-out will-change-[width] motion-reduce:transition-none"
    >
      {/* Brand */}
      <div
        className={`h-[72px] shrink-0 flex items-center border-b border-app-primary ${
          collapsed ? "px-5" : "justify-between px-5"
        }`}
      >
        <Link
          to={homeHref}
          aria-label="IRG home"
          className={`block overflow-hidden rounded-md ${focusRing} ${
            collapsed ? "h-10 w-10" : "h-9 w-[84px]"
          }`}
        >
          {/* The logo PNG has wide transparent margins; crop to the mark (collapsed) or wordmark. */}
          <img
            src="/assets/adminLogo.png"
            alt="IRG"
            className={`max-w-none dark:brightness-0 dark:invert ${
              collapsed ? "h-10 -ml-[22px]" : "h-9 -ml-[24px]"
            }`}
          />
        </Link>

        {!collapsed && (
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
            className={`p-1 rounded-lg text-app-muted hover:bg-app-sidebar-hover hover:text-app-secondary transition-colors ${focusRing} border`}
          >
            <ChevronLeft size={20} strokeWidth={1.75} />
          </button>
        )}
      </div>

      <nav
        aria-label="Main navigation"
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto overflow-x-hidden px-3 pt-6 pb-4 [scrollbar-width:thin] [scrollbar-color:var(--border-primary)_transparent]"
      >
        <ul className="flex flex-col gap-1">
          {menu.map((entry, index) =>
            entry.divider ? (
              <li
                key={`divider-${index}`}
                role="separator"
                className={`my-4 border-t border-app-primary ${collapsed ? "mx-2" : "mx-1"}`}
              />
            ) : entry.items ? (
              renderSection(entry)
            ) : (
              renderLink(entry)
            ),
          )}
        </ul>
      </nav>

      <div className="shrink-0 border-t border-app-primary px-3 py-2">
        {collapsed ? (
          <button
            type="button"
            onClick={() => setCollapsed(false)}
            aria-label="Expand sidebar"
            title="Expand sidebar"
            className={`ml-3 flex p-1.5 rounded-lg text-app-muted hover:bg-app-sidebar-hover hover:text-app-secondary transition-colors ${focusRing}`}
          >
            <ChevronRight size={20} strokeWidth={1.75} />
          </button>
        ) : (
          <p className="text-center text-[11px] text-app-muted">
            Developed with 💖 by Proviloops International.
          </p>
        )}
      </div>

      {/* Full-height toggle strip just outside the right border, so it never
          overlaps the nav scrollbar or any sidebar controls. */}
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        aria-label="Toggle sidebar"
        aria-expanded={!collapsed}
        className="group/edge absolute top-0 left-full h-full w-2 cursor-ew-resize select-none outline-none"
      >
        <span
          aria-hidden="true"
          className="pointer-events-none absolute left-0 top-0 h-full w-[2px] -translate-x-1/2 bg-transparent transition-colors duration-150 group-hover/edge:delay-100 group-hover/edge:bg-blue-500/50 group-focus-visible/edge:bg-blue-600 dark:group-hover/edge:bg-blue-400/50 dark:group-focus-visible/edge:bg-blue-400"
        />
      </button>
    </aside>
  );
}

export default SidebarComponent;
